import {CancelTokenSource} from "axios";
import {config, lead_form, theme} from "../config";
import {PAGE_SIZE} from "../const";
import {ReducerTypeHelper} from "../helpers/reducer";
import {ConfigState, LeadFormState} from "../contract/setting";
import {Theme} from '@emotion/react';

interface RouteStatus {
  articles: string[];
  articles_visible: string[];
  articles_ready: string[];
  articles_loading: {
    [key: string]: string[];
  };
  articles_failed: string[];
  articles_count: number;
  articles_unavailable: boolean;
}

export interface RouteMap {
  [key: string]: RouteStatus;
}

interface ApplicationState {
  authenticated_user_id: number;
  cancel_tokens: CancelTokenSource[];
  abort_controllers: AbortController[];
  config: ConfigState;
  lead_form: LeadFormState;
  loading: string[];
  offline: boolean;
  page_size: number;
  routes: RouteMap;
  theme: Theme;
}

const defaultState: ApplicationState = {
  authenticated_user_id: null,
  cancel_tokens: [],
  abort_controllers: [],
  config,
  lead_form,
  loading: [],
  offline: false,
  page_size: PAGE_SIZE,
  routes: {},
  theme,
}

const applicationReducer: ReducerTypeHelper<typeof defaultState> = (
  state = defaultState,
  action
) => {
  switch (action.type) {
    case 'application/pushCancelToken':
      return {
        ...state,
        cancel_tokens: [...state.cancel_tokens, action.cancel_token]
      }

    case 'application/pushAbortController':
      return {
        ...state,
        abort_controllers: [...state.abort_controllers, action.abort_controller]
      }

    case 'application/clearCancelTokens':
      return {
        ...state,
        cancel_tokens: []
      }

    case 'application/clearAbortControllers':
      return {
        ...state,
        abort_controllers: []
      }

    case 'application/setConfig':
      return {
        ...state,
        lead_form: {
          ...state.lead_form,
          ...action.lead_form
        },
        config: {
          ...state.config,
          ...action.config
        },
      }

    case 'application/setTheme':
      return {
        ...state,
        theme: {
          ...state.theme,
          ...action.theme
        },
      }

    case 'application/setRoutes':
      return {
        ...state,
        routes: {
          ...state.routes,
          ...action.routes,
        }
      }

    case 'application/setArticlesIndex':
      return {
        ...state,
        routes: {
          ...state.routes,
          [action.route]: {
            ...state.routes[action.route],
            articles: action.articles,
          }
        }
      }

    case 'application/setArticlesVisible':
      return {
        ...state,
        routes: {
          ...state.routes,
          [action.route]: {
            ...state.routes[action.route],
            articles_visible: action.articles,
          }
        }
      }

    case 'application/setArticlesReady':
      return {
        ...state,
        routes: {
          ...state.routes,
          [action.route]: {
            ...state.routes[action.route],
            articles_ready: action.articles,
          }
        }
      }

    case 'application/setArticlesLoading':
      return {
        ...state,
        routes: {
          ...state.routes,
          [action.route]: {
            ...state.routes[action.route],
            articles_loading: {
              ...state.routes[action.route]?.articles_loading,
              [action.chunk]: action.articles
            },
          }
        }
      }

    case 'application/setArticlesFailed':
      return {
        ...state,
        routes: {
          ...state.routes,
          [action.route]: {
            ...state.routes[action.route],
            articles_failed: action.articles,
          }
        }
      }

    case 'application/setArticlesCount':
      return {
        ...state,
        routes: {
          ...state.routes,
          [action.route]: {
            ...state.routes[action.route],
            articles_count: action.count,
          }
        }
      }

    case 'application/setArticlesUnavailable':
      return {
        ...state,
        routes: {
          ...state.routes,
          [action.route]: {
            ...state.routes[action.route],
            articles_unavailable: true
          }
        }
      }

    case 'application/setLoading':
      return {
        ...state,
        loading: action.loading,
      }

    case 'application/setOffline':
      return {
        ...state,
        offline: action.offline,
      }

    default:
      return state;
  }
}

export default applicationReducer;
